import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Button, Badge } from '@/components/ui/PageHeader';
import { DataTable, Pagination, type Column } from '@/components/ui/DataTable';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { SelectField } from '@/components/ui/FormFields';
import { AuthButton } from '@/components/ui/AuthButton';
import { useAuth } from '@/context/AuthContext';
import {
  listBoards,
  deleteBoard,
  getTrendingBoards,
  getFeaturedBoards,
  type NostalgiaBoard,
  type NostalgiaEra,
  type NostalgiaTheme,
  ERA_OPTIONS,
  THEME_OPTIONS,
} from '@/api/nostalgia';
import BoardForm from './BoardForm';

export default function BoardsList() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [boards, setBoards] = useState<NostalgiaBoard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [eraFilter, setEraFilter] = useState<string>('');
  const [themeFilter, setThemeFilter] = useState<string>('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBoard, setEditingBoard] = useState<NostalgiaBoard | null>(null);
  const [deletingBoard, setDeletingBoard] = useState<NostalgiaBoard | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchBoards = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    try {
      const response = await listBoards({
        page,
        limit: 20,
        era: eraFilter as NostalgiaEra || undefined,
        theme: themeFilter as NostalgiaTheme || undefined,
        sort: 'created_at',
        order: 'desc',
      });
      setBoards(response.data);
      setTotalPages(response.meta.totalPages);
    } catch (error) {
      console.error('Failed to fetch boards:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, page, eraFilter, themeFilter]);

  useEffect(() => {
    fetchBoards();
  }, [fetchBoards]);

  const handleDelete = async () => {
    if (!deletingBoard) return;

    setIsDeleting(true);
    try {
      await deleteBoard(deletingBoard.id);
      setDeletingBoard(null);
      fetchBoards();
    } catch (error) {
      console.error('Failed to delete board:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormSuccess = () => {
    setShowCreateModal(false);
    setEditingBoard(null);
    fetchBoards();
  };

  const columns: Column<NostalgiaBoard>[] = [
    {
      key: 'title',
      header: 'Title',
      render: (board) => (
        <div>
          <div className="font-medium text-gray-900">{board.title}</div>
          <div className="text-xs text-gray-500">{board.slug}</div>
        </div>
      ),
    },
    {
      key: 'era',
      header: 'Era',
      render: (board) => board.era ? (
        <Badge variant="purple">{board.era.replace('era_', '')}</Badge>
      ) : '-',
    },
    {
      key: 'theme',
      header: 'Theme',
      render: (board) => board.theme ? (
        <Badge variant="blue">{board.theme.replace('_', ' ')}</Badge>
      ) : '-',
    },
    {
      key: 'region',
      header: 'Region',
      render: (board) => board.region || '-',
    },
    {
      key: 'itemCount',
      header: 'Items',
      render: (board) => board.itemCount,
    },
    {
      key: 'viewCount',
      header: 'Views',
      render: (board) => board.viewCount,
    },
    {
      key: 'visibility',
      header: 'Visibility',
      render: (board) => (
        <Badge variant={board.visibility === 'everyone' ? 'green' : 'yellow'}>
          {board.visibility}
        </Badge>
      ),
    },
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-bold">Nostalgia Boards</h1>
            <AuthButton />
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800">Please login to manage boards.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <PageHeader
            title="Nostalgia Boards"
            subtitle="Create and manage memory boards"
            breadcrumbs={[
              { label: 'Nostalgia', href: '/lab/nostalgia' },
              { label: 'Boards' },
            ]}
          />
          <AuthButton />
        </div>

        <Shelves />

        {/* Filters */}
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-40">
              <SelectField
                label="Era"
                value={eraFilter}
                onChange={(e) => {
                  setEraFilter(e.target.value);
                  setPage(1);
                }}
                options={ERA_OPTIONS}
                placeholder="All Eras"
              />
            </div>
            <div className="w-40">
              <SelectField
                label="Theme"
                value={themeFilter}
                onChange={(e) => {
                  setThemeFilter(e.target.value);
                  setPage(1);
                }}
                options={THEME_OPTIONS}
                placeholder="All Themes"
              />
            </div>
            <div className="flex-1" />
            <Button onClick={() => setShowCreateModal(true)}>
              + New Board
            </Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border">
          <DataTable
            columns={columns}
            data={boards}
            keyField="id"
            isLoading={isLoading}
            emptyMessage="No boards found. Create your first board!"
            onRowClick={(board) => navigate(`/nostalgia/boards/${board.id}`)}
            actions={(board) => (
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingBoard(board);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeletingBoard(board);
                  }}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Delete
                </button>
              </div>
            )}
          />
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>

        {/* Create Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Create Board"
          size="lg"
        >
          <BoardForm onSuccess={handleFormSuccess} onCancel={() => setShowCreateModal(false)} />
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={!!editingBoard}
          onClose={() => setEditingBoard(null)}
          title="Edit Board"
          size="lg"
        >
          {editingBoard && (
            <BoardForm
              board={editingBoard}
              onSuccess={handleFormSuccess}
              onCancel={() => setEditingBoard(null)}
            />
          )}
        </Modal>

        {/* Delete Confirmation */}
        <ConfirmDialog
          isOpen={!!deletingBoard}
          onClose={() => setDeletingBoard(null)}
          onConfirm={handleDelete}
          title="Delete Board"
          message={`Are you sure you want to delete "${deletingBoard?.title}"? This will also delete all items and comments.`}
          confirmText="Delete"
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Two shelves that are NOT the same list sorted differently.
//
//   trending  — computed from what people are doing right now
//   featured  — chosen by a human
//
// Keeping them apart is what stops "featured" quietly becoming "popular",
// which is how an editorial surface loses the only thing it was for.
// ═══════════════════════════════════════════════════════════════════

function Shelves() {
  const [trending, setTrending] = useState<NostalgiaBoard[]>([]);
  const [featured, setFeatured] = useState<NostalgiaBoard[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [t, f] = await Promise.all([getTrendingBoards(), getFeaturedBoards()]);
        setTrending(t.data.boards ?? []);
        setFeatured(f.data.boards ?? []);
      } catch {
        // Public reads; a failure here should not take the page down.
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  if (!loaded) return null;

  return (
    <div className="grid md:grid-cols-2 gap-4 mb-6">
      <Shelf title="Trending" hint="computed from what people are doing" boards={trending} />
      <Shelf title="Featured" hint="chosen by a human" boards={featured} />
    </div>
  );
}

function Shelf({
  title,
  hint,
  boards,
}: {
  title: string;
  hint: string;
  boards: NostalgiaBoard[];
}) {
  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="flex items-baseline gap-2 mb-2">
        <h2 className="font-semibold text-gray-900">{title}</h2>
        <span className="text-xs text-gray-500">{hint}</span>
      </div>
      {!boards.length ? (
        <p className="text-sm text-gray-500">Nothing on this shelf yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {boards.slice(0, 5).map((b) => (
            <li key={b.id} className="text-sm flex items-center gap-2">
              <a
                href={`/lab/nostalgia/boards/${b.id}`}
                className="text-gray-900 hover:underline truncate"
              >
                {b.title}
              </a>
              {b.era && <Badge variant="purple">{b.era.replace('era_', '')}</Badge>}
              <span className="text-xs text-gray-400 ml-auto shrink-0">
                {b.itemCount} items
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
